import * as R from "ramda";
import { translateCategory } from "common/app/command-palette/translation";
import {
  Category,
  Command,
  CommandGroup,
} from "common/app/command-palette/types";

const DEFAULT_CATEGORY_KEY = "uncategorized";

/**
 * Groups a flat list of commands by their category and sorts commands within each group by label.
 * Commands without a category are placed under a default category (e.g., "uncategorized").
 * Group labels are generated by translating the category key using `translateCategory`.
 *
 * @param commands - The flat array of Command objects. Handles null/undefined input gracefully.
 * @returns An array of CommandGroup objects, structured for display (e.g., in react-select),
 * or an empty array if the input is invalid.
 */
export function groupCommands(commands: Command[] | undefined): CommandGroup[] {
  if (!Array.isArray(commands)) {
    return [];
  }

  if (commands.length === 0) {
    return [];
  }

  return R.pipe(
    R.groupBy(
      (command: Command): Category => command.category ?? DEFAULT_CATEGORY_KEY,
    ),
    R.toPairs,
    R.map(
      ([category, categoryCommands]: [Category, Command[]]): CommandGroup => ({
        id: category,
        label: translateCategory(category),
        options: R.sortBy(R.prop("label"), categoryCommands),
      }),
    ),
  )(commands);
}
